import type { EntitySnapshot, Pools } from '../net/protocol';
import { TINT_HUE, type TintFamily } from './tint';

/**
 * What is on a body, and how to show it. Phase 32.6, from
 * `doc/grind/design/STATUS-EFFECT-DISPLAY.md`.
 *
 * Pure, and deliberately: the ordering rule, the countdown formatting and the kind→colour mapping
 * are the parts worth pinning, and none of them needs a DOM. What *does* need a real browser is
 * whether the row is on screen and whether the hover card lands over the action bar — jsdom computes
 * no layout, so those are `scripts/browser/`'s job and not a unit test's.
 */

/** The four crowd-control kinds the simulation can apply. Matches `game.CCKind`; see §5. */
export type DebuffKind = 'stun' | 'root' | 'silence' | 'slow';

/**
 * The buffs. **One, and it is the first the game has ever had** — `STATUS-EFFECT-DISPLAY.md` §2
 * shipped saying *"There is no buff system at all… half of buffs and debuffs has nothing to
 * display"*, and Phase 39's Mana Shield toggle is what finally fills that half.
 *
 * It is a *stance* rather than a timed effect: on until the player toggles it off or the pool
 * cannot cover a blow, so it has no duration at all. That is the one structural difference the row
 * had to grow for.
 */
export type BuffKind = 'manaShield';

export type EffectKind = DebuffKind | BuffKind;

/** One effect on one body, with what is left of it. */
export interface ActiveEffect {
  kind: EffectKind;
  /**
   * Which half of the row this is. Drives the **frame colour** and nothing else — the spec's §3
   * decision, taken so buffs and debuffs share one row and one slot geometry rather than needing a
   * second row that is empty half the time.
   */
  polarity: 'buff' | 'debuff';
  /**
   * Seconds remaining, or **null when the effect has no duration**.
   *
   * Null is not "zero" and the difference matters: zero means expired and null means indefinite, and
   * an indefinite effect must draw no sweep and no countdown rather than drawing an empty one. A
   * stance that rendered as a permanently-full timer would be telling the player it is about to run
   * out forever.
   */
  seconds: number | null;
}

/**
 * Which wire field carries which kind.
 *
 * **One table, read in both directions** — `activeEffects` walks it to build the list and
 * `statusEffects.test.ts` walks it to check every kind is covered. A field added on the server and
 * forgotten here is an effect the player is under and the HUD cannot see, which is the same
 * hand-mirrored-vocabulary fault the Go side guards with
 * `TestEveryCrowdControlKindReachesTheWire`.
 */
export const EFFECT_FIELD: Record<DebuffKind, keyof EntitySnapshot> = {
  stun: 'stunSec',
  root: 'rootSec',
  silence: 'silenceSec',
  slow: 'slowSec',
};

/**
 * The colour each kind wears, drawn from the item palette rather than invented.
 *
 * `TINT_HUE` is ten families chosen to be nameable and **separable** — adjacent entries at least
 * 30° apart — and already shared between inventory icons and the spell FX, so a fire weapon and a
 * fire bolt are the same red. A status palette of its own would be a third colour vocabulary in one
 * client.
 *
 * **A slow takes `cold`, and that is a coincidence of content rather than a rule.** Every slow in
 * the shipped catalogue is applied by a frost skill, so blue reads correctly today. `CCState` keeps
 * one expiry per kind and **no provenance** (ADR 0007), so a slow genuinely cannot say what applied
 * it — the day a poison skill slows something, this line is the one that becomes a lie, and the fix
 * is a provenance field the server does not have. Recorded in the spec's §4 as the owner's call.
 *
 * There is no green here because **nothing in the game can apply poison**: no `POISON` damage type,
 * no damage-over-time. `poison` has a hue in `TINT_HUE` and would be a colour that never appears.
 */
export const EFFECT_FAMILY: Record<EffectKind, TintFamily> = {
  stun: 'physical',
  root: 'defence',
  silence: 'arcane',
  slow: 'cold',
  // Mana Shield takes `arcane` because that is the family `mana_flat` already wears in the item
  // tint table, and the shield *is* the mana pool defending you. It shares the hue with the
  // silence, which is deliberate rather than overlooked: the two are told apart by the **frame**
  // — one buff-green, one debuff-red — which is exactly the job the spec gave frame colour, and a
  // second blue invented to avoid the collision would be a colour that means nothing.
  manaShield: 'arcane',
};

export const EFFECT_HUE: Record<EffectKind, number> = {
  stun: TINT_HUE[EFFECT_FAMILY.stun],
  root: TINT_HUE[EFFECT_FAMILY.root],
  silence: TINT_HUE[EFFECT_FAMILY.silence],
  slow: TINT_HUE[EFFECT_FAMILY.slow],
  manaShield: TINT_HUE[EFFECT_FAMILY.manaShield],
};

/** What the player is told it is called. */
export const EFFECT_NAME: Record<EffectKind, string> = {
  stun: 'Stunned',
  root: 'Rooted',
  silence: 'Silenced',
  slow: 'Chilled',
  manaShield: 'Mana Shield',
};

/**
 * What it does, **in the simulation's terms rather than the skill description's**.
 *
 * A root says *cannot move* and not *cannot act*, because a rooted caster still casts — that is the
 * whole difference between root and stun, and a tooltip that blurred it would teach the player the
 * wrong lesson about which one to fear. The slow's number is filled in from `moveScale`, so the card
 * says what the server is actually doing rather than what a skill promised.
 */
export const EFFECT_BODY: Record<EffectKind, string> = {
  stun: 'You cannot move or act.',
  root: 'You cannot move. Casting still works.',
  silence: 'You cannot cast. Weapon attacks still work.',
  slow: 'Your movement is slowed.',
  // Filled in with the real percentage by `effectDetail`; this is the fallback for a frame that
  // somehow reports the stance on without a figure.
  manaShield: 'Incoming damage is paid from mana instead of health.',
};

/**
 * The placeholder glyph, until the icon sheet exists.
 *
 * **A letter is an honest placeholder and a wrong icon is not.** Four cells on a 4×4 sheet is one
 * `slice-icons.py` run away, and until it lands a box with a letter in it says *"this is a
 * placeholder"* to anyone looking, where a borrowed skill icon would say *"this is the art"*. Each
 * is the initial of the name above except the chill, which takes `C` so it cannot be read as the
 * silence's `S`.
 */
export const EFFECT_LETTER: Record<EffectKind, string> = {
  stun: 'S',
  root: 'R',
  silence: 'Z',
  slow: 'C',
  manaShield: 'M',
};

/**
 * What is on this body, longest-remaining first.
 *
 * **Longest first, and the intuitive order is the wrong one.** Sorting by what expires soonest puts
 * the icon a player is watching in a slot that moves as things tick down — and the row is under the
 * cursor precisely when they are reading it. Longest-first is stable for as long as anything is
 * stable.
 *
 * Ties break by kind order, so two effects with the same remaining time do not swap places between
 * frames on a float comparison.
 */
export function activeEffects(
  snap: EntitySnapshot | undefined,
  pools?: Pools,
): ActiveEffect[] {
  const out: ActiveEffect[] = [];

  // Buffs first, and they come from a different frame. A debuff is on a body, so it rides the
  // snapshot every entity gets; a stance is the player's own state and rides the CHARACTER frame.
  // Only the player's row is ever drawn (§2 — what is on a *monster* is said by tinting it), so
  // reading two sources here costs nothing and keeps each field where it belongs.
  if (pools && typeof pools.manaShieldPct === 'number' && pools.manaShieldPct > 0) {
    out.push({ kind: 'manaShield', polarity: 'buff', seconds: null });
  }

  if (snap) {
    for (const kind of DEBUFF_ORDER) {
      const seconds = snap[EFFECT_FIELD[kind]];
      if (typeof seconds === 'number' && seconds > 0) {
        out.push({ kind, polarity: 'debuff', seconds });
      }
    }
  }

  return out.sort(compareEffects);
}

/**
 * The row's order: indefinite effects first, then the longest-remaining.
 *
 * **The spec says longest-remaining first and gives the reason** — the row sits under the cursor
 * exactly when it is being read, so an order that reshuffles as things tick is the one that moves
 * the icon a player is reaching for. An effect with *no* duration never ticks, so it is the most
 * stable thing that can be in the row, and putting it at the stable end is that same rule carried
 * through rather than a second rule bolted beside it.
 *
 * Ties break by kind order, so two effects with the same remaining time cannot swap places between
 * frames on a float comparison.
 */
function compareEffects(a: ActiveEffect, b: ActiveEffect): number {
  if (a.seconds === null && b.seconds === null) return KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
  if (a.seconds === null) return -1;
  if (b.seconds === null) return 1;
  return b.seconds - a.seconds || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind);
}

/** The debuff tie-break order, and the order the tables above are written in. */
export const DEBUFF_ORDER: DebuffKind[] = ['stun', 'root', 'silence', 'slow'];

/** Every kind, buffs first — the tie-break order for the row as a whole. */
export const KIND_ORDER: EffectKind[] = ['manaShield', ...DEBUFF_ORDER];

/**
 * The countdown text, or null when the number is noise.
 *
 * **A number only under ten seconds.** A duration is the thing a player is waiting on, and `1.4` is
 * worth reading where `47.0` is a decoration that costs the icon its legibility. One decimal,
 * because tenths are what a stun is measured in.
 */
export function countdownText(seconds: number): string | null {
  if (seconds <= 0 || seconds >= 10) return null;
  return seconds.toFixed(1);
}

/**
 * How much of the sweep is left, in [0, 1].
 *
 * **The server sends what is left and never what it started at**, so there is nothing to divide by:
 * a full sweep would need the original duration, which is a second field carried on every frame to
 * animate one icon. Instead the sweep is read against a fixed window — the same trick the action
 * bar's cooldown uses for a skill whose total it does not know — so a long effect starts full and a
 * short one starts part-way, which is honest about what is known rather than inventing a total.
 */
export const SWEEP_WINDOW_SEC = 5;

export function sweepFraction(seconds: number): number {
  if (seconds <= 0) return 0;
  return Math.min(1, seconds / SWEEP_WINDOW_SEC);
}

/**
 * The tooltip's third line, filled in from what the server is doing.
 *
 * `moveScale` is absent when unslowed and absent means ×1, so the percentage is only ever shown for
 * a slow that is actually reported — never computed from a default that would read as a fact.
 */
export function effectDetail(
  kind: EffectKind,
  snap: EntitySnapshot | undefined,
  pools?: Pools,
): string {
  // The shield's whole point is the *number*, and the client holds no catalogue — which is why the
  // server sends the percentage rather than a bool. Said in the simulation's terms: mana is spent
  // 1:1 with the damage routed, and the stance drops when the pool cannot cover a blow, so both
  // facts a player needs in order to decide whether to keep it up are here.
  if (kind === 'manaShield') {
    const pct = pools?.manaShieldPct;
    if (typeof pct !== 'number' || pct <= 0) return EFFECT_BODY.manaShield;
    return `${Math.round(pct)}% of incoming damage is paid from mana instead of health. Ends if mana runs out.`;
  }
  if (kind !== 'slow') return EFFECT_BODY[kind];
  const scale = snap?.moveScale;
  if (typeof scale !== 'number' || scale <= 0 || scale >= 1) return EFFECT_BODY.slow;
  return `You move at ${Math.round(scale * 100)}% speed.`;
}
